import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { GroupIcons } from '../../../components/SideMenu/icons';
import type {
  SideMenuGroup,
  SideMenuItem,
} from '../../../components/SideMenu/types';
import { usePermissions } from '../../shared/usePermissions';
import { API_ANALYTICS_URL } from '../../../utils/Constants';
import { getAnalyticsBaseUrl } from './analyticsNavigation';
import { useAnalyticsNavigation } from './useAnalyticsNavigation';

const findActiveKey = (
  groups: SideMenuGroup[],
  pathname: string,
): string | null => {
  const allItems = groups.flatMap((group) => group.items);
  const directGroupMatch = groups.find((group) => group.path === pathname);

  if (directGroupMatch) return directGroupMatch.key;

  const exactMatch = allItems.find((item) => item.path === pathname);
  if (exactMatch) return exactMatch.key;

  const prefixMatch = allItems
    .filter((item) => pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];

  return prefixMatch?.key ?? null;
};

/**
 * Build the side menu from the user's permissions.
 *
 * Each item is paired with a boolean from `usePermissions` so the
 * structure mirrors the role table 1:1 — adding a new role-gated section
 * means adding a permission flag, not branching here.
 */
export const useSideMenu = () => {
  const permissions = usePermissions();
  const location = useLocation();
  const openAnalytics = useAnalyticsNavigation();

  const groups = useMemo<SideMenuGroup[]>(() => {
    const result: SideMenuGroup[] = [];

    const visible = (
      items: Array<SideMenuItem & { visible: boolean }>,
    ): SideMenuItem[] =>
      items
        .filter((item) => item.visible)
        .map(({ visible: _visible, ...rest }) => rest);

    if (permissions.canViewJournals) {
      const tasks = visible([
        {
          key: 'tasks_outgoing',
          label: 'Исходящие вызовы',
          path: '/calls/outgoing',
          visible: true,
        },
        {
          key: 'tasks_incoming',
          label: 'Входящие вызовы',
          path: '/calls/incoming',
          visible: true,
        },
      ]);
      if (tasks.length) {
        result.push({
          key: 'tasks',
          label: 'Журнал заданий',
          icon: GroupIcons.tasks,
          items: tasks,
        });
      }
    }

    if (permissions.canViewStatistics) {
      result.push({
        key: 'statistics',
        label: 'Статистика',
        icon: GroupIcons.statistics,
        items: [
          {
            key: 'stats_outgoing',
            label: 'Исходящие вызовы',
            path: '/statistics/outgoing',
          },
          {
            key: 'stats_incoming',
            label: 'Входящие вызовы',
            path: '/statistics/incoming',
          },
        ],
      });
    }

    const analyticsBaseUrl = getAnalyticsBaseUrl(API_ANALYTICS_URL);
    if (permissions.canViewAnalytics && analyticsBaseUrl) {
      result.push({
        key: 'analytics',
        label: 'Аналитика',
        icon: GroupIcons.analytics,
        items: [
          {
            key: 'analytics_outbound',
            label: 'Исходящие вызовы',
            path: analyticsBaseUrl + 'outbound',
            external: true,
            onOpen: () => openAnalytics(analyticsBaseUrl + 'outbound'),
          },
          {
            key: 'analytics_inbound',
            label: 'Входящие вызовы',
            path: analyticsBaseUrl + 'inbound',
            external: true,
            onOpen: () => openAnalytics(analyticsBaseUrl + 'inbound'),
          },
        ],
      });
    }

    const settingsItems = visible([
      {
        key: 'settings_sip',
        label: 'SIP-аккаунты',
        path: '/sip-accounts',
        visible: permissions.canViewSipAccounts,
      },
      {
        key: 'settings_users',
        label: 'Пользователи',
        path: '/settings/users',
        visible: permissions.canViewUsers,
      },
      {
        key: 'settings_calls',
        label: 'Звонки',
        path: '/settings/calls',
        visible: permissions.canViewCallSettings,
      },
      {
        key: 'settings_callpools',
        label: 'Пулы обзвона',
        path: '/settings/callpools',
        visible: permissions.canViewCallPools,
      },
      {
        key: 'settings_blacklist',
        label: 'Черный список',
        path: '/blacklist',
        visible: permissions.canViewBlacklist,
      },
      {
        key: 'settings_questionnaire_templates',
        label: 'Шаблоны анкет',
        path: '/questionnaire-templates',
        visible: permissions.canViewSurveyTemplates,
      },
    ]);
    if (permissions.canViewSettingsMenu && settingsItems.length) {
      result.push({
        key: 'settings',
        label: 'Настройки',
        icon: GroupIcons.settings,
        placement: 'bottom',
        items: settingsItems,
      });
    }

    if (permissions.canViewProfile) {
      result.push({
        key: 'logout',
        label: 'Выход',
        icon: GroupIcons.logout,
        path: '/logout',
        placement: 'bottom',
        items: [],
      });
    }

    return result;
  }, [permissions, openAnalytics]);

  const activeKey = useMemo(
    () => findActiveKey(groups, location.pathname),
    [groups, location.pathname],
  );

  return { groups, activeKey };
};
